import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
} from '@mui/material';
import AddDentistFormContainer from './add-dentist-form.container';
import { useFormik } from 'formik';
import { User } from 'models/user';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getUserByEmailTemplate, whiteLabelDentistAccessTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { permissions } from 'utils/permission';
import { useTranslation } from 'react-i18next';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import LinkUserInfo from 'components/users/shared/link-user-info.component';
import { CustomerSetting } from 'models/customer-setting';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type AddDentistModalProps = {
    handleClose: () => void;
    open: boolean;
    showAddDentistButton: boolean;
    handleDentistCreation: (dentist: User) => void;
    clinicId: string;
    shouldCreateWLUser: boolean;
    labClientCustomerSettings: CustomerSetting[];
    reFetchClient?: () => void;
    onUserLinked: (user: User) => void;
    initialLabDefaultClinicId?: string;
};

// TODO: This could maybe be shared with the AddDentist modal used in user list?
const AddDentistModal: FC<AddDentistModalProps> = ({
    handleDentistCreation,
    clinicId,
    handleClose,
    open,
    showAddDentistButton,
    shouldCreateWLUser = false,
    labClientCustomerSettings,
    reFetchClient,
    onUserLinked,
    initialLabDefaultClinicId,
}: AddDentistModalProps) => {
    const { t: translate } = useTranslation(['orders', 'users', 'client-directory']);
    const [activeStep, setActiveStep] = useState(0);
    const [notAllowedMsg, setNotAllowedMsg] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const [user, setUser] = useState<User>({} as User);
    const [isIsolatedUsersEnable, setIsIsolatedUsersEnable] = useState(false);
    const [isAccessGranted, setIsAccessGranted] = useState(false);
    const lab = currentOrgVar();

    const [getUserByEmail, { loading }] = useLazyQuery(getUserByEmailTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted: ({ userByEmail }) => {
            if (!userByEmail) {
                setActiveStep(1);
                return;
            }
            setUser(userByEmail);
            if (userByEmail.organization?._id === clinicId) {
                // User already exists in organization
                setActiveStep(3);
                if (shouldCreateWLUser && userByEmail) {
                    const hasWhiteLabelDentistAccess = userByEmail.whiteLabelAccess?.some(
                        (elem: string) => elem?.toString() === lab._id?.toString(),
                    );
                    setIsAccessGranted(hasWhiteLabelDentistAccess || false);
                }
                return;
            }

            if (userByEmail.isShared) {
                // No allowed
                setNotAllowedMsg('NOT_ALLOWED_SHARED');
                setActiveStep(4);
                return;
            }

            if (
                permissions.isDentist(userByEmail.type) ||
                (permissions.isWhiteLabeledOrganization(userByEmail.organization) &&
                    permissions.isDentist(userByEmail.type))
            ) {
                const hasIsolatedUsers = customerSettings.getCustomerSetting(
                    userByEmail.organization.customerSettings || [],
                    CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
                );
                if (hasIsolatedUsers) {
                    // No allowed
                    setNotAllowedMsg('NOT_ALLOWED_CLINIC');
                    setActiveStep(4);
                    return;
                }
                // Link
                setActiveStep(2);
                return;
            }
            // No allowed
            setNotAllowedMsg('NOT_ALLOWED');
            setActiveStep(4);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [whiteLabelDentistAccess] = useMutation(whiteLabelDentistAccessTemplate, {
        onCompleted: () => {
            if (reFetchClient) {
                reFetchClient();
                setUser({} as User);
                setIsAccessGranted(false);
                handleClose();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const whiteListDomains: string[] =
        customerSettings.getCustomerSetting(
            labClientCustomerSettings || [],
            CONSTANTS.SETTINGS_NAMES.ADD_USER_WHITELIST_DOMAINS,
        ) || [];

    const validate = (values: { email: string }) => {
        const errors: Record<string, string> = {};
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (whiteListDomains.length) {
            const [_, enteredEmailDomain] = values.email?.split('@');
            if (!whiteListDomains.includes(enteredEmailDomain?.toUpperCase())) {
                errors.email = translate('users:VALIDATION:EMAIL_DOMAIN');
            }
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validate,
        onSubmit: (values: { email: string }) => {
            getUserByEmail({ variables: { email: values.email } });
        },
    });

    const moveToFirstStep = () => {
        setActiveStep(0);
    };

    const handleWhiteLabelDentistAccess = async (dentistId: string) => {
        await whiteLabelDentistAccess({ variables: { dentistId: dentistId } });
    };

    const closeDialog = () => {
        moveToFirstStep();
        handleClose();
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <Fragment>
                        <form onSubmit={formik.handleSubmit}>
                            <DialogContent>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <FormControl margin="dense" fullWidth>
                                            <TextField
                                                id="email"
                                                label={translate('common:EMAIL')}
                                                required
                                                type="email"
                                                helperText={formik.touched.email && formik.errors.email}
                                                error={formik.touched.email && Boolean(formik.errors.email)}
                                                {...formik.getFieldProps('email')}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <LoadingButton type="submit" loading={loading}>
                                    {translate('common:NEXT_STEP')}
                                </LoadingButton>
                            </DialogActions>
                        </form>
                    </Fragment>
                );
            case 1:
                return (
                    <DialogContent>
                        <AddDentistFormContainer
                            handleDentistCreation={handleDentistCreation}
                            handleClose={closeDialog}
                            showAddDentistButton={showAddDentistButton}
                            clinicId={clinicId}
                            email={formik.values.email || ''}
                            shouldCreateWLUser={shouldCreateWLUser}
                            isIsolatedUsersEnable={isIsolatedUsersEnable}
                            labClientCustomerSettings={labClientCustomerSettings}
                            initialLabDefaultClinicId={initialLabDefaultClinicId}
                        />
                    </DialogContent>
                );
            case 2:
                return (
                    <LinkUserInfo
                        userType="DENTIST"
                        user={user}
                        organization={clinicId}
                        role="DENTIST_USER"
                        onBack={moveToFirstStep}
                        onUserLinked={onUserLinked}
                    />
                );
            case 3:
                return (
                    <DialogContent>
                        {shouldCreateWLUser && !isAccessGranted ? (
                            <Box>
                                <Alert severity="warning">
                                    {translate(`client-directory:EXISTS_USER_GRANT_ACCESS`)}
                                </Alert>
                            </Box>
                        ) : (
                            <Box>
                                <Alert severity="warning">{translate(`users:USER_ALREADY_EXISTS`)}</Alert>
                            </Box>
                        )}
                        <DialogActions>
                            <Button onClick={moveToFirstStep}>{translate('common:BACK')}</Button>
                            {shouldCreateWLUser && !isAccessGranted ? (
                                <Button onClick={() => handleWhiteLabelDentistAccess(user._id)}>
                                    {translate('common:ACTIVATE')}
                                </Button>
                            ) : undefined}
                        </DialogActions>
                    </DialogContent>
                );
            case 4:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:${notAllowedMsg}`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={moveToFirstStep}>{translate('common:BACK')}</Button>
                        </DialogActions>
                    </DialogContent>
                );
            default:
                return 'Unknown Step';
        }
    };

    useEffect(() => {
        // Reset on dialog open
        if (open) {
            formik.resetForm();
            setNotAllowedMsg('');
            setUser({} as User);
            const hasIsolatedUsers = customerSettings.getCustomerSetting(
                labClientCustomerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
            );
            if (hasIsolatedUsers) {
                setActiveStep(1);
                setIsIsolatedUsersEnable(true);
            } else {
                setIsIsolatedUsersEnable(false);
            }
        }
    }, [open]);

    return (
        <Dialog fullWidth maxWidth="md" onClose={closeDialog} open={open}>
            <DialogTitle>{translate('orders:NEW_DENTIST')}</DialogTitle>
            {shouldCreateWLUser || showAddDentistButton ? getStepContent(activeStep) : getStepContent(1)}
        </Dialog>
    );
};

export default AddDentistModal;
